import { apiClient, type ApiResponse } from './client';

export interface MachinePayload {
    name: string;
    model?: string | null;
    workCenterId?: string | null;
    capacityPerHour?: number | null;
    status: 'RUNNING' | 'IDLE' | 'MAINTENANCE' | 'BREAKDOWN';
    lastMaintenanceDate?: string | null;
    maintenanceFrequencyDays?: number | null;
    isActive?: boolean;
    warehouseId?: string | null;
    machineCode?: string | null;
    make?: string | null;
    serialNumber?: string | null;
    installationDate?: string | null;
    warrantyExpiryDate?: string | null;
    machineHourRate?: number | null;
    /** Operators who run this machine (linked both ways); left out = keep the current ones */
    operatorIds?: string[];
}

export interface MachineResponse {
    id: string;
    name: string;
    model: string | null;
    work_center_id: string | null;
    work_center_name?: string | null;
    work_center_code?: string | null;
    capacity_per_hour: number | null;
    status: 'RUNNING' | 'IDLE' | 'MAINTENANCE' | 'BREAKDOWN';
    last_maintenance_date: string | null;
    maintenance_frequency_days: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    warehouse_id?: string | null;
    warehouse_name?: string | null;
    machine_code?: string | null;
    make?: string | null;
    serial_number?: string | null;
    installation_date?: string | null;
    warranty_expiry_date?: string | null;
    machine_hour_rate?: string | number | null;
    assigned_operator_ids?: string[] | null;
}

const BASE = '/api/v1/machines';

// Asset fields: undefined = leave unchanged on update, null/'' = clear
const assetFields = (payload: Partial<MachinePayload>) => ({
    machine_code: payload.machineCode !== undefined ? (payload.machineCode || null) : undefined,
    make: payload.make !== undefined ? (payload.make || null) : undefined,
    serial_number: payload.serialNumber !== undefined ? (payload.serialNumber || null) : undefined,
    installation_date: payload.installationDate !== undefined ? (payload.installationDate || null) : undefined,
    warranty_expiry_date: payload.warrantyExpiryDate !== undefined ? (payload.warrantyExpiryDate || null) : undefined,
    machine_hour_rate: payload.machineHourRate !== undefined ? payload.machineHourRate : undefined,
    operator_ids: payload.operatorIds,
});

export async function getNextMachineCode(): Promise<ApiResponse<{ machine_code: string }>> {
    const { data } = await apiClient.get<ApiResponse<{ machine_code: string }>>(`${BASE}/next-code`);
    return data;
}

export async function createMachine(
    payload: MachinePayload,
): Promise<ApiResponse<MachineResponse>> {
    const formattedPayload = {
        name: payload.name,
        model: payload.model || null,
        work_center_id: payload.workCenterId || null,
        capacity_per_hour: payload.capacityPerHour !== undefined ? payload.capacityPerHour : null,
        status: payload.status,
        last_maintenance_date: payload.lastMaintenanceDate || null,
        maintenance_frequency_days: payload.maintenanceFrequencyDays !== undefined ? payload.maintenanceFrequencyDays : null,
        is_active: payload.isActive,
        warehouse_id: payload.warehouseId || null,
        ...assetFields(payload),
    };
    const { data } = await apiClient.post<ApiResponse<MachineResponse>>(
        `${BASE}/create`,
        formattedPayload,
    );
    return data;
}

export async function getAllMachines(warehouseId?: string): Promise<
    ApiResponse<MachineResponse[]>
> {
    const params = warehouseId ? { warehouse_id: warehouseId } : undefined;
    const { data } = await apiClient.get<ApiResponse<MachineResponse[]>>(
        `${BASE}/`,
        { params },
    );
    return data;
}

export async function updateMachine(
    id: string,
    payload: Partial<MachinePayload>,
): Promise<ApiResponse<MachineResponse>> {
    const formattedPayload = {
        name: payload.name,
        model: payload.model !== undefined ? (payload.model || null) : undefined,
        work_center_id: payload.workCenterId !== undefined ? (payload.workCenterId || null) : undefined,
        capacity_per_hour: payload.capacityPerHour !== undefined ? payload.capacityPerHour : undefined,
        status: payload.status,
        last_maintenance_date: payload.lastMaintenanceDate !== undefined ? (payload.lastMaintenanceDate || null) : undefined,
        maintenance_frequency_days: payload.maintenanceFrequencyDays !== undefined ? payload.maintenanceFrequencyDays : undefined,
        is_active: payload.isActive,
        warehouse_id: payload.warehouseId !== undefined ? (payload.warehouseId || null) : undefined,
        ...assetFields(payload),
    };
    const { data } = await apiClient.put<ApiResponse<MachineResponse>>(
        `${BASE}/${id}`,
        formattedPayload,
    );
    return data;
}

export async function deleteMachine(id: string): Promise<ApiResponse<null>> {
    const { data } = await apiClient.delete<ApiResponse<null>>(`${BASE}/${id}`);
    return data;
}

export async function assignOperatorsToMachine(
    id: string,
    operatorIds: string[],
): Promise<ApiResponse<MachineResponse>> {
    const { data } = await apiClient.put<ApiResponse<MachineResponse>>(
        `${BASE}/${id}/operators`,
        { operator_ids: operatorIds },
    );
    return data;
}
